import React from 'react';

import { Box, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';

export const VerdictMark: React.FC<{ passed: boolean; size?: number }> = ({
  passed,
  size = 20,
}) => (
  <Box
    aria-hidden="true"
    sx={{
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      width: `${size / 16}rem`,
      height: `${size / 16}rem`,
      borderRadius: '50%',
      position: 'relative',
      // `currentColor` at 25% as a layer, not a colour: the ring sits on whatever
      // ground the caller uses, and fading the box would take the glyph with it.
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        opacity: 0.25,
      },
      '& > *': { position: 'relative' },
    }}
  >
    {passed ? (
      <CheckRoundedIcon sx={{ fontSize: `${(size * 0.7) / 16}rem` }} />
    ) : (
      <CloseRoundedIcon sx={{ fontSize: `${(size * 0.7) / 16}rem` }} />
    )}
  </Box>
);

export const VerdictBlock: React.FC<{ passed: boolean; label: string }> = ({
  passed,
  label,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1.5,
      py: 1.25,
      borderRadius: 1,
      color: 'common.white',
      backgroundColor: passed ? 'highlight.scoreGreenStrong' : 'primary.main',
    }}
  >
    <VerdictMark passed={passed} />
    <Typography variant="body1" fontWeight={500} color="inherit">
      {label}
    </Typography>
  </Box>
);

export type Verdict = 'passed' | 'failed' | 'undecided';

const CHIP: Record<
  Verdict,
  { bg: string; color: string; icon: React.ReactNode }
> = {
  passed: {
    bg: 'highlight.scoreGreenStrong',
    color: 'common.white',
    icon: <ThumbUpRoundedIcon sx={{ fontSize: '0.875rem' }} />,
  },
  failed: {
    bg: 'text.danger',
    color: 'common.white',
    icon: <CloseRoundedIcon sx={{ fontSize: '0.875rem' }} />,
  },
  undecided: {
    bg: 'highlight.neutral',
    color: 'text.secondary',
    icon: <QueryBuilderRoundedIcon sx={{ fontSize: '0.875rem' }} />,
  },
};

export const VerdictChip: React.FC<{ verdict: Verdict; label: string }> = ({
  verdict,
  label,
}) => {
  const style = CHIP[verdict];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontSize: '0.8rem',
        fontWeight: 500,
        lineHeight: 1.5,
        backgroundColor: style.bg,
        color: style.color,
      }}
    >
      <Box component="span" aria-hidden="true" display="inline-flex">
        {style.icon}
      </Box>
      {label}
    </Box>
  );
};
